/**
 * 把後端回傳的錯誤（尤其是 Laravel 驗證錯誤物件）轉成使用者看得懂的中文句子。
 *
 * 後端驗證失敗時會回傳類似：
 *   { "cost_items.6.quantity": ["The cost_items.6.quantity field is required."], ... }
 * 直接 JSON.stringify 出來是機器語言，客戶看不明白，
 * 這裡統一轉成：「成本項目 第 7 項：數量、單價、金額 必須填寫」。
 */

// 陣列型欄位（例如報價單的成本項目），顯示時會加上「第 N 項」
const GROUP_LABELS = {
  cost_items: "成本項目",
  receipt_items: "收據項目",
  refund_items: "退款項目",
  expense_items: "支出項目",
  salary_items: "薪金項目",
  salary_records: "薪金記錄",
  reservation_items: "預約項目",
  payment_steps: "付款階段",
  payment_proofs: "付款證明",
  transactions: "交易記錄",
  documents: "文件",
  files: "檔案",
  items: "項目",
  stages: "階段",
  workers: "師傅",
  user_ids: "用戶",
  bills: "帳單",
};

const FIELD_LABELS = {
  // 通用
  id: "編號",
  name: "名稱",
  name_cn: "中文名稱",
  name_en: "英文名稱",
  title: "標題",
  description: "描述",
  item_description: "項目說明",
  remark: "備註",
  notes: "筆記",
  reason: "原因",
  content: "內容",
  address: "地址",
  size: "尺寸",
  status: "狀態",
  sort_order: "排序",
  is_active: "啟用狀態",
  file: "檔案",
  files: "檔案",
  image_path: "圖片",
  file_name: "檔案名稱",

  // 金額與數量
  amount: "金額",
  total: "總數",
  total_amount: "總金額",
  total_cost: "總成本",
  quantity: "數量",
  unit_price: "單價",
  cost: "成本",
  deposit_amount: "訂金金額",
  payment_amount: "付款金額",
  withdrawal_amount: "提款金額",
  prepayment_amount: "預付金額",
  step_amount: "階段金額",
  step_percentage: "階段百分比",
  step_name: "階段名稱",
  step_phase: "階段",
  percentage: "百分比",
  balance: "餘額",
  min_amount: "最低金額",
  max_amount: "最高金額",
  empty_rows: "空白行數",

  // 日期
  date: "日期",
  date_from: "開始日期",
  date_to: "結束日期",
  start_date: "開始日期",
  end_date: "結束日期",
  pay_date: "支付日期",
  payment_date: "付款日期",
  invoice_date: "發票日期",
  receipt_date: "收據日期",
  quotation_date: "報價日期",
  refund_date: "退款日期",
  expense_date: "支出日期",
  bill_date: "帳單日期",
  bad_debt_date: "壞賬日期",
  complete_date: "完成日期",
  info_date: "日期",
  booking_time: "預約時間",
  submission_date: "提交日期",
  reservation_date: "預約日期",
  salary_month: "薪金月份",
  salary_year: "薪金年份",
  join_date: "入職日期",
  date_of_birth: "出生日期",

  // 關聯
  customer_id: "客戶",
  user_id: "用戶",
  project_id: "項目",
  invoice_id: "發票",
  receipt_id: "收據",
  quotation_id: "報價單",
  product_id: "產品",
  product_category_id: "產品分類",
  supplier_id: "供應商",
  bank_id: "銀行",
  worker_id: "師傅",
  approve_id: "審批人",
  payer_id: "付款人",
  salesperson_id: "銷售員",
  template_id: "模版",
  stamp_id: "印章",
  remark_id: "備註",
  payment_method_id: "付款方式",
  receive_method_id: "收款方式",
  salary_receive_method_id: "出糧方式",
  user_type_id: "用戶類型",
  project_stage_id: "工程階段",
  prepayment_id: "預付款",
  claim_id: "報銷",
  expense_id: "支出",
  bill_id: "帳單",
  coupon_id: "優惠券",

  // 帳戶
  email: "電郵",
  phone: "電話",
  phone2: "後備電話",
  mobile: "手機",
  password: "密碼",
  old_password: "舊密碼",
  password_confirmation: "確認密碼",
  c_password: "確認密碼",
  vertification_code: "驗證碼",
  code: "代碼",
  role_name: "角色名稱",
  position: "職位",
  company_name: "公司名稱",

  // 單號
  invoice_number: "發票編號",
  receipt_number: "收據編號",
  quotation_number: "報價單編號",
  order_number: "訂單編號",
  bill_number: "帳單編號",
  expense_number: "支出編號",
  check_number: "支票號碼",

  // 薪金
  day_salary: "日薪",
  total_salary: "總薪金",
  gross_salary: "應發薪金",
  actual_salary: "實發薪金",
  allowance: "津貼",
  commission: "佣金",
  ot_amount: "加班金額",
  ot_hours: "加班時數",
  mpf_employee: "強積金（僱員）",
  mpf_employer: "強積金（僱主）",
  working_date: "工作日期",
  working_days: "工作日數",
};

// 由後端的英文驗證訊息推斷原因，轉成中文說法
const RULE_PATTERNS = [
  [/field is required|is required/i, "必須填寫"],
  [/must be a number|must be numeric|must be an integer/i, "必須為數字"],
  [/must be a date|is not a valid date/i, "必須為有效日期"],
  [/must be a valid email|must be a valid e-?mail/i, "格式不正確"],
  [/must be a string/i, "格式不正確"],
  [/must be an array/i, "格式不正確"],
  [/must be true or false|must be a boolean/i, "格式不正確"],
  [/has already been taken/i, "已被使用，請改用其他值"],
  [/do(es)? not match|must match|confirmation does not match|must be the same/i, "兩次輸入不一致"],
  [/selected .* is invalid|does not exist/i, "所選的項目不存在，請重新選擇"],
  [/(may not be|must not be) greater than (\d+)/i, "超出長度或數值上限"],
  [/must be at least (\d+)/i, "低於最少長度或數值"],
  [/must be after/i, "日期須在開始日期之後"],
  [/invalid file type|mimes|must be a file of type/i, "檔案格式不支援"],
];

const MAX_LINES = 8;

const humanizeRule = (rawMessage) => {
  const text = String(rawMessage || "");
  const hit = RULE_PATTERNS.find(([pattern]) => pattern.test(text));
  return hit ? hit[1] : "格式不正確";
};

const labelForField = (field) =>
  FIELD_LABELS[field] || GROUP_LABELS[field] || field;

// "cost_items.6.quantity" → { group: "成本項目 第 7 項", field: "數量" }
const parseFieldKey = (key) => {
  const segments = String(key).split(".");
  const groupParts = [];

  for (let i = 0; i < segments.length - 1; i += 1) {
    const segment = segments[i];
    if (/^\d+$/.test(segment)) {
      groupParts.push(`第 ${Number(segment) + 1} 項`);
    } else {
      groupParts.push(labelForField(segment));
    }
  }

  return {
    group: groupParts.join(" "),
    field: labelForField(segments[segments.length - 1]),
  };
};

const joinUnique = (values) => [...new Set(values)].join("、");

/**
 * 把 Laravel 驗證錯誤物件轉成中文條列。
 * @param {Object} errors 形如 { "cost_items.6.quantity": ["..."] }
 * @returns {string[]}
 */
export const formatValidationErrors = (errors) => {
  // group → rule → [field]
  const grouped = new Map();

  Object.keys(errors).forEach((key) => {
    const { group, field } = parseFieldKey(key);
    const raw = errors[key];
    const rule = humanizeRule(Array.isArray(raw) ? raw[0] : raw);

    if (!grouped.has(group)) grouped.set(group, new Map());
    const rules = grouped.get(group);
    if (!rules.has(rule)) rules.set(rule, []);
    rules.get(rule).push(field);
  });

  const lines = [];
  grouped.forEach((rules, group) => {
    rules.forEach((fields, rule) => {
      const fieldText = `${joinUnique(fields)} ${rule}`;
      lines.push(group ? `${group}：${fieldText}` : fieldText);
    });
  });

  if (lines.length <= MAX_LINES) return lines;
  return [
    ...lines.slice(0, MAX_LINES),
    `⋯⋯另有 ${lines.length - MAX_LINES} 項問題，請逐項檢查。`,
  ];
};

const STATUS_FALLBACK = {
  400: "提交的資料不正確，請檢查後再試。",
  403: "你沒有權限執行此操作。",
  404: "找不到相關記錄。",
  413: "上傳的檔案過大。",
  422: "提交的資料不正確，請檢查後再試。",
  500: "系統發生錯誤，請稍後再試或聯絡技術支援。",
};

/**
 * 把 API 錯誤轉成可直接顯示給用戶的中文訊息。
 * @param {Object} error axios 錯誤物件
 * @returns {string}
 */
export const formatApiError = (error) => {
  const response = error && error.response;

  if (!response) {
    return "無法連接伺服器，請檢查網絡後再試。";
  }

  const message = response.data && response.data.message;

  if (message && typeof message === "object") {
    const lines = formatValidationErrors(message);
    if (lines.length > 0) {
      return `請修正以下資料：\n${lines.map((line) => `• ${line}`).join("\n")}`;
    }
  }

  if (typeof message === "string" && message.trim()) {
    return message;
  }

  return STATUS_FALLBACK[response.status] || "操作失敗，請稍後再試。";
};

export default formatApiError;
